import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { register } from "services/auth.service";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AppButton } from "components/common/buttons";
import { AppPasswordField, AppTextField } from "components/common/inputs";

const theme = createTheme({
  typography: {
    fontFamily: "Inter, Arial",
  },
  palette: {
    primary: {
      main: "#4680FF",
    },
  },
});

function RegisterPage() {
  const [form, setForm] = useState({
    userName: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "error",
  });
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!form.userName)
      newErrors.userName = "Tên đăng nhập không được để trống.";
    if (!form.fullName) newErrors.fullName = "Họ tên không được để trống.";
    if (!form.email) newErrors.email = "Email không được để trống.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      newErrors.email = "Email không hợp lệ.";
    if (!form.password) newErrors.password = "Mật khẩu không được để trống.";
    else if (form.password.length < 6)
      newErrors.password = "Mật khẩu tối thiểu 6 ký tự.";
    if (!form.confirmPassword)
      newErrors.confirmPassword = "Vui lòng nhập lại mật khẩu.";
    else if (form.password !== form.confirmPassword)
      newErrors.confirmPassword = "Mật khẩu nhập lại không khớp.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await register(form);
      setAlert({
        show: true,
        message: "Đăng ký thành công! Vui lòng đăng nhập.",
        severity: "success",
      });
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          "Đăng ký thất bại. Vui lòng thử lại.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        {/* Background wrapper */}
        <Box
          sx={{
            position: "relative",
            minHeight: "100vh",
            backgroundColor: "#f5f5f5",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            boxSizing: "border-box",
            pt: { xs: 3, sm: 4 },
            px: 2,
          }}
        >
          {/* Top-left gradient */}
          <Box
            sx={{
              position: "absolute",
              top: "-20%",
              left: "-20%",
              width: "50vw",
              height: "50vw",
              background:
                "radial-gradient(circle at top left, rgba(70,128,255,0.4), transparent 70%)",
              filter: "blur(120px)",
              zIndex: 0,
            }}
          />
          {/* Middle gradient - Orange */}
          <Box
            sx={{
              position: "absolute",
              left: "30%",
              top: "20%",
              width: "40vw",
              height: "40vw",
              background:
                "radial-gradient(circle at center, rgba(255,140,0,0.35), transparent 70%)",
              filter: "blur(100px)",
              zIndex: 0,
            }}
          />
          {/* Bottom-right gradient */}
          <Box
            sx={{
              position: "absolute",
              bottom: "-20%",
              right: "-20%",
              width: "50vw",
              height: "50vw",
              background:
                "radial-gradient(circle at bottom right, rgba(70,128,255,0.4), transparent 70%)",
              filter: "blur(120px)",
              zIndex: 0,
            }}
          />

          {/* Grid container */}
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ position: "relative", zIndex: 1, my: "auto", width: "100%" }}
          >
            <Grid item xs={12} sm={8} md={5} lg={4} xl={3}>
              <Box
                sx={{
                  textAlign: "center",
                  mb: { xs: 2, sm: 3 },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    marginBottom: { xs: "0.5rem", sm: "1rem", md: "1.5rem" },
                  }}
                >
                  {/* Logo */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/logoXNTD.png"
                      alt="Logo Xí nghiệp Trắc địa"
                      style={{
                        height: "80px",
                        width: "auto",
                      }}
                    />
                  </Box>

                  {/* App Name */}
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#4680FF",
                      fontWeight: 600,
                      fontSize: { xs: "1.8rem", sm: "2rem" },
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    HỆ THỐNG
                    <br />
                    QUẢN LÝ XÍ NGHIỆP TRẮC ĐỊA
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  maxWidth: "400px",
                  backgroundColor: "white",
                  p: { xs: 3, sm: 4 },
                  borderRadius: 2,
                  boxShadow: 0,
                  mx: "auto",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="h4"
                  align="center"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.2rem", sm: "1.75rem" },
                    color: "#023047",
                    fontWeight: 600,
                  }}
                >
                  Đăng ký tài khoản
                </Typography>
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  <AppTextField
                    fullWidth
                    label="Tên đăng nhập"
                    name="userName"
                    variant="outlined"
                    size="medium"
                    value={form.userName}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, userName: value }))
                    }
                    error={!!errors.userName}
                    helperText={errors.userName}
                    style={{ marginBottom: "0px" }}
                  />
                  <AppTextField
                    fullWidth
                    label="Họ và tên"
                    name="fullName"
                    variant="outlined"
                    size="medium"
                    value={form.fullName}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, fullName: value }))
                    }
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    style={{ marginBottom: "0px" }}
                  />
                  <AppTextField
                    fullWidth
                    label="Email"
                    name="email"
                    variant="outlined"
                    size="medium"
                    value={form.email}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, email: value }))
                    }
                    error={!!errors.email}
                    helperText={errors.email}
                    style={{ marginBottom: "0px" }}
                  />
                  <AppPasswordField
                    fullWidth
                    name="password"
                    label="Mật khẩu"
                    variant="outlined"
                    size="medium"
                    value={form.password}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, password: value }))
                    }
                    error={!!errors.password}
                    helperText={errors.password}
                    style={{ marginBottom: "0px" }}
                  />
                  <AppPasswordField
                    fullWidth
                    name="confirmPassword"
                    label="Nhập lại mật khẩu"
                    variant="outlined"
                    size="medium"
                    value={form.confirmPassword}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, confirmPassword: value }))
                    }
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    style={{ marginBottom: "0px" }}
                  />
                  <AppButton
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      mt: { xs: 1, sm: 2 },
                      py: { xs: 1, sm: 1.5 },
                    }}
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : "Đăng ký"}
                  </AppButton>
                </Box>
                <Typography
                  align="center"
                  mt={2}
                  sx={{ fontSize: { xs: "15px", sm: "13px" } }}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={0.5}
                >
                  Đã có tài khoản?
                  <AppButton
                    variant="text"
                    href="/login"
                    sx={{
                      mx: 0,
                      p: 0,
                    }}
                  >
                    Đăng nhập
                  </AppButton>
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* App description bottom - nằm dưới luồng flex, không dùng fixed để tránh đè chữ */}
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              pt: { xs: 3, sm: 4 },
              pb: 1,
              zIndex: 2,
              position: "relative",
            }}
          >
            <Typography
              sx={{ color: "#515151ff", fontSize: "0.95rem", fontWeight: 600 }}
            >
              © {new Date().getFullYear()} Tổ Nghiên cứu và Phát triển - Xí nghiệp
              Trắc địa
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
      <Snackbar
        open={alert.show}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, show: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, show: false })}
          severity={alert.severity}
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            color:
              alert.severity === "info"
                ? "orange"
                : alert.severity === "error"
                  ? "error.main"
                  : alert.severity === "success"
                    ? "success.main"
                    : "text.primary",
            borderColor:
              alert.severity === "info"
                ? "orange"
                : alert.severity === "error"
                  ? "error.light"
                  : alert.severity === "success"
                    ? "success.light"
                    : "text.secondary",
            "& .MuiAlert-icon": {
              color: alert.severity === "info" ? "orange" : undefined,
            },
          }}
          variant="outlined"
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default RegisterPage;
